import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { getEncoding } from "js-tiktoken";
import { useEffect, useMemo, useRef } from "react";
import { useMutation, useSubscription } from "urql";
import { useImmerReducer } from "use-immer";
import { v4 as uuidv4 } from "uuid";
import { useUserCurrent } from "../CurrentUser";
import { chunkedUpload } from "../chunkedUploader";
import { getFileType, IMAGE_FILE_TYPES } from "../utils";
import type { IFormValues as AttachFileFormValues } from "./AttachFileForm";
import type { IFormValues as AttachWebpageFormValues } from "./AttachWebpageForm";
import {
    askAiMutation,
    attachFileMutation,
    attachKnowledgeMutation,
    attachTranscriptionMutation,
    attachWebpageMutation,
    reAskAiMutation,
    type ThreadAiEventsSubscription,
    type ThreadAiEventsSubscriptionVariables,
    type ThreadAskAiMutation,
    type ThreadAskAiMutationVariables,
    type ThreadAttachFileMutation,
    type ThreadAttachFileMutationVariables,
    type ThreadAttachKnowledgeMutation,
    type ThreadAttachKnowledgeMutationVariables,
    type ThreadAttachTranscriptionMutation,
    type ThreadAttachTranscriptionMutationVariables,
    type ThreadAttachWebpageMutation,
    type ThreadAttachWebpageMutationVariables,
    type ThreadReAskAiMutation,
    type ThreadReAskAiMutationVariables,
    threadAiEventsSubscription,
} from "./queries";
import {
    buildPersonalityInput,
    createOptimisticUserItem,
    mapLiveItemToThreadItem,
    replaceThreadItemsFromConnection,
    type ThreadData,
    type ThreadItemModel,
    type ThreadPersonality,
} from "./threadItemModel";

const encoding = getEncoding("cl100k_base");

interface ActiveRun {
    clientRunId: string;
}

interface AttachFetchingState {
    file: boolean;
    knowledge: boolean;
    transcription: boolean;
    webpage: boolean;
}

interface AskAiInput {
    content: string;
    imageFile?: File | null;
}

interface ThreadControllerState {
    threadName: string | null;
    personality: ThreadPersonality;
    threadItems: ThreadItemModel[];
    uploading: boolean;
    activeRun: ActiveRun | null;
    processedSequence: number;
    runFailed: boolean;
    rollbackItems: ThreadItemModel[] | null;
}

type ThreadControllerAction =
    | { type: "RESET_FROM_THREAD"; thread: ThreadData }
    | { type: "SET_PERSONALITY"; personality: ThreadPersonality }
    | { type: "SET_UPLOADING"; uploading: boolean }
    | { type: "START_LIVE_RUN"; clientRunId: string; rollbackItems: ThreadItemModel[] }
    | { type: "APPEND_OPTIMISTIC_USER_ITEM"; content: string; key: string }
    | { type: "TRUNCATE_AFTER_ITEM"; itemId: number }
    | {
          type: "APPEND_LIVE_ITEM";
          item: NonNullable<ThreadAiEventsSubscription["threadAiEvents"]["item"]>;
          key: string;
          sequence: number;
      }
    | { type: "ROLLBACK_LIVE_RUN" }
    | { type: "COMPLETE_LIVE_RUN" }
    | { type: "REPLACE_THREAD_ITEMS"; threadItems: ThreadItemModel[] }
    | { type: "SET_THREAD_NAME"; threadName: string | null };

export interface UseThreadControllerResult {
    threadName: string | null;
    personality: ThreadPersonality;
    setPersonality: (personality: ThreadPersonality) => void;
    threadItems: ThreadItemModel[];
    tokenCount: number;
    loadingText: string;
    isOwner: boolean;
    isSendingMessage: boolean;
    attachFetching: AttachFetchingState;
    askAi: (data: AskAiInput) => Promise<void>;
    reAskAi: (item: ThreadItemModel) => Promise<void>;
    attachFile: (values: AttachFileFormValues) => Promise<void>;
    attachKnowledge: (documentId: number) => Promise<void>;
    attachTranscription: (transcriptionId: number) => Promise<void>;
    attachWebpage: (values: AttachWebpageFormValues) => Promise<void>;
}

function buildInitialState(thread: ThreadData): ThreadControllerState {
    return {
        threadName: thread.threadName,
        personality: thread.aiPersonality!,
        threadItems: replaceThreadItemsFromConnection(thread.threadItems),
        uploading: false,
        activeRun: null,
        processedSequence: 0,
        runFailed: false,
        rollbackItems: null,
    };
}

function threadControllerReducer(draft: ThreadControllerState, action: ThreadControllerAction) {
    switch (action.type) {
        case "RESET_FROM_THREAD":
            return buildInitialState(action.thread);
        case "SET_PERSONALITY":
            draft.personality = action.personality;
            break;
        case "SET_UPLOADING":
            draft.uploading = action.uploading;
            break;
        case "START_LIVE_RUN":
            draft.activeRun = { clientRunId: action.clientRunId };
            draft.processedSequence = 0;
            draft.runFailed = false;
            draft.rollbackItems = action.rollbackItems;
            break;
        case "APPEND_OPTIMISTIC_USER_ITEM":
            // Optimistic and streamed items do not have stable database ids yet, so they need
            // deterministic local keys until the final persisted mutation result replaces them.
            draft.threadItems.push(createOptimisticUserItem(action.content, action.key));
            break;
        case "TRUNCATE_AFTER_ITEM": {
            const idx = draft.threadItems.findIndex((item) => item.id === action.itemId);
            return {
                ...draft,
                threadItems: draft.threadItems.slice(0, idx + 1),
            };
        }
        case "APPEND_LIVE_ITEM":
            draft.processedSequence = action.sequence;
            draft.threadItems.push(mapLiveItemToThreadItem(action.item, action.key));
            break;
        case "ROLLBACK_LIVE_RUN":
            if (draft.rollbackItems !== null) {
                draft.threadItems = draft.rollbackItems;
            }
            draft.runFailed = true;
            draft.activeRun = null;
            draft.rollbackItems = null;
            break;
        case "COMPLETE_LIVE_RUN":
            draft.activeRun = null;
            draft.rollbackItems = null;
            break;
        case "REPLACE_THREAD_ITEMS":
            draft.threadItems = action.threadItems;
            break;
        case "SET_THREAD_NAME":
            draft.threadName = action.threadName;
            break;
    }
}

function useTokenCount(personality: ThreadPersonality, threadItems: ThreadItemModel[]): number {
    const threadString = useMemo(() => {
        return `${personality.systemPrompt}\n\n${threadItems.map((item) => item.content).join("\n\n")}`;
    }, [personality.systemPrompt, threadItems]);
    const [debouncedString] = useDebouncedValue(threadString, 300);

    return useMemo(() => {
        return encoding.encode(debouncedString).length;
    }, [debouncedString]);
}

function getLoadingText({
    uploading,
    askAiFetching,
    reAskAiFetching,
    attachFileFetching,
    attachKnowledgeFetching,
    attachTranscriptionFetching,
    attachWebpageFetching,
}: {
    uploading: boolean;
    askAiFetching: boolean;
    reAskAiFetching: boolean;
    attachFileFetching: boolean;
    attachKnowledgeFetching: boolean;
    attachTranscriptionFetching: boolean;
    attachWebpageFetching: boolean;
}): string {
    if (uploading) {
        return "Uploading file...";
    }
    if (askAiFetching) {
        return "AI is thinking hard...";
    }
    if (reAskAiFetching) {
        return "AI is thinking hard...";
    }
    if (attachFileFetching) {
        return "Processing document...";
    }
    if (attachKnowledgeFetching) {
        return "Processing document...";
    }
    if (attachTranscriptionFetching) {
        return "Processing transcript...";
    }
    if (attachWebpageFetching) {
        return "Fetching website...";
    }
    return "";
}

function useThreadController(thread: ThreadData): UseThreadControllerResult {
    const [state, dispatch] = useImmerReducer(threadControllerReducer, thread, buildInitialState);
    // Async mutation handlers need the latest reducer state after awaits so they do not
    // double-handle failures that were already processed by the live subscription effect.
    const latestStateRef = useRef(state);
    const previousThreadIdRef = useRef(thread.id);
    const [askAiResult, executeAskAi] = useMutation<ThreadAskAiMutation, ThreadAskAiMutationVariables>(askAiMutation);
    const [reAskAiResult, executeReAskAi] = useMutation<ThreadReAskAiMutation, ThreadReAskAiMutationVariables>(
        reAskAiMutation,
    );
    const [attachFileResult, executeAttachFile] = useMutation<
        ThreadAttachFileMutation,
        ThreadAttachFileMutationVariables
    >(attachFileMutation);
    const [attachKnowledgeResult, executeAttachKnowledge] = useMutation<
        ThreadAttachKnowledgeMutation,
        ThreadAttachKnowledgeMutationVariables
    >(attachKnowledgeMutation);
    const [attachWebpageResult, executeAttachWebpage] = useMutation<
        ThreadAttachWebpageMutation,
        ThreadAttachWebpageMutationVariables
    >(attachWebpageMutation);
    const [attachTranscriptionResult, executeAttachTranscription] = useMutation<
        ThreadAttachTranscriptionMutation,
        ThreadAttachTranscriptionMutationVariables
    >(attachTranscriptionMutation);
    const [threadAiEventsResult] = useSubscription<
        ThreadAiEventsSubscription,
        ThreadAiEventsSubscription,
        ThreadAiEventsSubscriptionVariables
    >({
        query: threadAiEventsSubscription,
        pause: state.activeRun === null,
        variables: {
            threadId: thread.id,
            clientRunId: state.activeRun?.clientRunId ?? "",
        },
    });

    useEffect(() => {
        latestStateRef.current = state;
    }, [state]);

    useEffect(() => {
        if (previousThreadIdRef.current === thread.id) {
            return;
        }

        // Keep local edits when the current thread query refreshes, but fully reset when
        // the user navigates to a different thread id.
        previousThreadIdRef.current = thread.id;
        dispatch({ type: "RESET_FROM_THREAD", thread });
    }, [dispatch, thread]);

    const currentUser = useUserCurrent();
    const tokenCount = useTokenCount(state.personality, state.threadItems);
    const isOwner = currentUser?.id === thread.user?.id;
    const isSendingMessage = state.uploading || askAiResult.fetching;
    const attachFetching = {
        file: attachFileResult.fetching,
        knowledge: attachKnowledgeResult.fetching,
        transcription: attachTranscriptionResult.fetching,
        webpage: attachWebpageResult.fetching,
    };
    const loadingText = getLoadingText({
        uploading: state.uploading,
        askAiFetching: askAiResult.fetching,
        reAskAiFetching: reAskAiResult.fetching,
        attachFileFetching: attachFetching.file,
        attachKnowledgeFetching: attachFetching.knowledge,
        attachTranscriptionFetching: attachFetching.transcription,
        attachWebpageFetching: attachFetching.webpage,
    });

    useEffect(() => {
        const event = threadAiEventsResult.data?.threadAiEvents;
        if (!event) {
            return;
        }

        // The backend can replay buffered events when the subscription attaches late.
        // Ignore duplicate sequence numbers so the transient live list stays append-only.
        if (event.sequence <= state.processedSequence) {
            return;
        }

        if (event.kind === "ITEM" && event.item) {
            dispatch({
                type: "APPEND_LIVE_ITEM",
                item: event.item,
                key: `live:${event.clientRunId}:${event.sequence}`,
                sequence: event.sequence,
            });
            return;
        }

        if (event.kind === "FAILED") {
            dispatch({ type: "ROLLBACK_LIVE_RUN" });
            notifications.show({
                title: "AI error",
                message: event.error ?? "AI run failed",
            });
            return;
        }

        if (event.kind === "COMPLETED") {
            dispatch({ type: "COMPLETE_LIVE_RUN" });
        }
    }, [dispatch, state.processedSequence, threadAiEventsResult.data]);

    const waitForSubscriptionTick = async () => {
        // Let React commit the active run and Urql attach the websocket subscription
        // before the mutation starts publishing live events.
        await new Promise<void>((resolve) => {
            window.setTimeout(() => {
                resolve();
            }, 0);
        });
    };

    const askAi = async ({ content, imageFile }: AskAiInput) => {
        if (!content && !imageFile) {
            return;
        }

        let imageData: ThreadAskAiMutationVariables["data"]["image"] | null = null;
        if (imageFile) {
            const fileType = getFileType(imageFile);
            if (!fileType || !IMAGE_FILE_TYPES.includes(fileType)) {
                notifications.show({
                    title: "Unsupported image",
                    message: "Please upload a PNG, JPEG, or WebP image.",
                    color: "red",
                });
                return;
            }

            try {
                dispatch({ type: "SET_UPLOADING", uploading: true });
                const storedName = await chunkedUpload({ file: imageFile });
                imageData = {
                    file: storedName,
                    fileName: imageFile.name,
                    fileType,
                };
            } catch (error) {
                let message: string;
                if (error instanceof Error) {
                    message = error.message;
                } else {
                    message = "Unknown error";
                }
                notifications.show({
                    title: "Failed to upload image",
                    message,
                    color: "red",
                });
                dispatch({ type: "SET_UPLOADING", uploading: false });
                return;
            } finally {
                dispatch({ type: "SET_UPLOADING", uploading: false });
            }
        }

        const rollbackItems = state.threadItems.slice();
        const clientRunId = uuidv4();
        dispatch({ type: "START_LIVE_RUN", clientRunId, rollbackItems });
        dispatch({
            type: "APPEND_OPTIMISTIC_USER_ITEM",
            content,
            key: `optimistic:${clientRunId}:user`,
        });

        const data: ThreadAskAiMutationVariables["data"] = {
            threadId: thread.id,
            clientRunId,
            role: "USER",
            content,
            image: imageData ?? undefined,
            personalityData: buildPersonalityInput(state.personality),
        };

        try {
            await waitForSubscriptionTick();
            const result = await executeAskAi({ data });
            if (result.error && !latestStateRef.current.runFailed) {
                dispatch({ type: "ROLLBACK_LIVE_RUN" });
                notifications.show({
                    title: "AI error",
                    message: result.error.message,
                });
            }
            if (result.data) {
                // Live subscription items are transient UI hints. The mutation result remains
                // the source of truth because it reflects the persisted thread state.
                dispatch({
                    type: "REPLACE_THREAD_ITEMS",
                    threadItems: replaceThreadItemsFromConnection(result.data.threadAskAi.threadItems),
                });
                dispatch({
                    type: "SET_THREAD_NAME",
                    threadName: result.data.threadAskAi.threadName,
                });
            }
        } finally {
            dispatch({ type: "COMPLETE_LIVE_RUN" });
        }
    };

    const reAskAi = async (item: ThreadItemModel) => {
        if (item.id === null) {
            return;
        }

        const rollbackItems = state.threadItems.slice();
        const clientRunId = uuidv4();
        dispatch({ type: "START_LIVE_RUN", clientRunId, rollbackItems });
        dispatch({ type: "TRUNCATE_AFTER_ITEM", itemId: item.id });

        const data: ThreadReAskAiMutationVariables["data"] = {
            itemId: item.id,
            clientRunId,
            personalityData: buildPersonalityInput(state.personality),
        };

        try {
            await waitForSubscriptionTick();
            const result = await executeReAskAi({ data });
            if (result.error && !latestStateRef.current.runFailed) {
                dispatch({ type: "ROLLBACK_LIVE_RUN" });
                notifications.show({
                    title: "AI error",
                    message: result.error.message,
                });
            }
            if (result.data) {
                // Re-ask replaces the live stream with the final persisted thread items so the UI
                // cannot keep stale intermediate events after the run completes.
                dispatch({
                    type: "REPLACE_THREAD_ITEMS",
                    threadItems: replaceThreadItemsFromConnection(result.data.threadReAskAi.threadItems),
                });
                dispatch({
                    type: "SET_THREAD_NAME",
                    threadName: result.data.threadReAskAi.threadName,
                });
            }
        } finally {
            dispatch({ type: "COMPLETE_LIVE_RUN" });
        }
    };

    const attachFile = async (values: AttachFileFormValues) => {
        let storedName: string;
        try {
            dispatch({ type: "SET_UPLOADING", uploading: true });
            storedName = await chunkedUpload({ file: values.file! });
        } catch (error) {
            let message: string;
            if (error instanceof Error) {
                message = error.message;
            } else {
                message = "Unknown error";
            }
            notifications.show({
                title: "Failed to upload file",
                message,
                color: "red",
            });
            return;
        } finally {
            dispatch({ type: "SET_UPLOADING", uploading: false });
        }

        const data: ThreadAttachFileMutationVariables["data"] = {
            threadId: thread.id,
            fileType: getFileType(values.file!)!,
            fileName: values.file!.name,
            file: storedName,
        };
        const result = await executeAttachFile({ data });
        if (result.error) {
            notifications.show({
                title: "Attach file failed",
                message: result.error.message,
            });
        }
        if (result.data) {
            dispatch({
                type: "REPLACE_THREAD_ITEMS",
                threadItems: replaceThreadItemsFromConnection(result.data.threadAttachFile.threadItems),
            });
        }
    };

    const attachWebpage = async (values: AttachWebpageFormValues) => {
        const data: ThreadAttachWebpageMutationVariables["data"] = {
            threadId: thread.id,
            url: values.url,
        };
        const result = await executeAttachWebpage({ data });
        if (result.error) {
            notifications.show({
                title: "Attach webpage failed",
                message: result.error.message,
            });
        }
        if (result.data) {
            dispatch({
                type: "REPLACE_THREAD_ITEMS",
                threadItems: replaceThreadItemsFromConnection(result.data.threadAttachWebpage.threadItems),
            });
        }
    };

    const attachKnowledge = async (documentId: number) => {
        const result = await executeAttachKnowledge({ documentId, threadId: thread.id });
        if (result.error) {
            notifications.show({
                title: "Attach Document failed",
                message: result.error.message,
            });
        }
        if (result.data) {
            dispatch({
                type: "REPLACE_THREAD_ITEMS",
                threadItems: replaceThreadItemsFromConnection(result.data.threadAttachKnowledge.threadItems),
            });
        }
    };

    const attachTranscription = async (transcriptionId: number) => {
        const result = await executeAttachTranscription({ transcriptionId, threadId: thread.id });
        if (result.error) {
            notifications.show({
                title: "Attach Transcript failed",
                message: result.error.message,
            });
        }
        if (result.data) {
            dispatch({
                type: "REPLACE_THREAD_ITEMS",
                threadItems: replaceThreadItemsFromConnection(result.data.threadAttachTranscription.threadItems),
            });
        }
    };

    const setPersonality = (personality: ThreadPersonality) => {
        dispatch({ type: "SET_PERSONALITY", personality });
    };

    return {
        threadName: state.threadName,
        personality: state.personality,
        setPersonality,
        threadItems: state.threadItems,
        tokenCount,
        loadingText,
        isOwner,
        isSendingMessage,
        attachFetching,
        askAi,
        reAskAi,
        attachFile,
        attachKnowledge,
        attachTranscription,
        attachWebpage,
    };
}

export default useThreadController;
